import {
  useEffect,
  useRef,
  useState,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from "react";
import MessageBubble from "./MessageBubble";
import { FlowChoice, MessageType } from "@/types";
import AnimatedText from "./AnimatedText";
import { getThemeConfig } from "../theme/theme.manager";

/**
 * メッセージコンテナのクラス
 */
const messageContainerClasses = "flex-1 overflow-y-auto pt-2.5 px-1 relative";

/**
 * メッセージコンテナのプロパティ
 */
interface MessageContainerProps {
  messages: Array<
    MessageType & {
      onLike: () => void;
      onDislike: () => void;
      isAnswering?: boolean;
      botName?: string;
      onFlowChoice?: (choice: FlowChoice) => void;
    }
  >;
  isThinking: boolean;
  isThinkingText?: string;
  isAnswering?: boolean;
  onScrollChange?: (isScrolledUp: boolean) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  isResetSession?: boolean;
}

/**
 * Publicのメソッド
 */
export interface MessageContainerRef {
  scrollToBottom: (behavior?: ScrollBehavior) => void;
}

/**
 * メッセージコンテナ
 */
const MessageContainer = forwardRef<MessageContainerRef, MessageContainerProps>(
  (
    {
      messages,
      isThinking,
      isThinkingText,
      isAnswering = false,
      onScrollChange,
      onLoadMore,
      hasMore = false,
      isLoadingMore = false,
      isResetSession = false,     
    },
    ref
  ) => {
    const themeConfig = getThemeConfig();
    const containerRef = useRef<HTMLDivElement>(null);
    const messagesEndRef = useRef<HTMLInputElement>(null);

    const [shouldAutoScroll, setShouldAutoScroll] = useState(true);

    // 最下部までスクロールする
    const scrollToBottom = useCallback((behavior: ScrollBehavior = "auto") => {
      if (!containerRef.current) return;

      containerRef.current.scrollTop = containerRef.current.scrollHeight;
      if (behavior === "smooth") {
        requestAnimationFrame(() => {
          if (containerRef.current) {
            containerRef.current.scrollTop = containerRef.current.scrollHeight;
          }
        });
      }
    }, []);

    useImperativeHandle(ref, () => ({
      scrollToBottom,
    }));

    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      // スクロールイベント
      const handleScroll = () => {
        const { scrollTop, scrollHeight, clientHeight } = container;
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 50;

        setShouldAutoScroll(isNearBottom);
        if (onScrollChange) {
          onScrollChange(!isNearBottom);
        }

        // スクロートトップ近くにスクロールした場合
        if (scrollTop <= 50 && hasMore && !isLoadingMore && onLoadMore) {
          onLoadMore();
        }
      };

      // ホイールイベント
      const handleWheel = (e: WheelEvent) => {
        if (!hasMore || isLoadingMore || !onLoadMore) return;

        // 上へスクロールしてトップ近くにした場合
        if (e.deltaY < 0 && container.scrollTop <= 80) {
          onLoadMore();
        }
      };

      container.addEventListener("scroll", handleScroll);
      container.addEventListener("wheel", handleWheel, { passive: false });

      return () => {
        container.removeEventListener("scroll", handleScroll);
        container.removeEventListener("wheel", handleWheel);
      };
    }, [onScrollChange, hasMore, isLoadingMore, onLoadMore]);

    useEffect(() => {
      if (shouldAutoScroll && (messages.length > 0 || isThinking)) {
        scrollToBottom();
        setTimeout(() => scrollToBottom("smooth"), 100);
      }
    }, [messages, isThinking, shouldAutoScroll, scrollToBottom]);

    useEffect(() => {
      if (!containerRef.current || !shouldAutoScroll) return;

      const observer = new MutationObserver(() => {
        scrollToBottom();
      });

      observer.observe(containerRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true,
      });

      return () => observer.disconnect();
    }, [shouldAutoScroll, scrollToBottom]);

    return (
      <div
        ref={containerRef}
        className={`pathbee-message-container ${messageContainerClasses}`}
      >
        {hasMore && (
          <div className="text-center py-2 mb-2">
            {isLoadingMore && (
              <span
                className="text-sm"
                style={{ color: "var(--main-text)", opacity: 0.7 }}
              >
                履歴をロード中...
              </span>
            )}
          </div>
        )}

        {messages.map((msg) => (
          <div key={msg.id} className="pathbee-animate-fade-in">
            <MessageBubble {...msg} />
          </div>
        ))}

        {isThinking && (
          <div className="pathbee-animate-fade-in">
            <MessageBubble
              id="isThinkingTextID"
              isThinking={isThinking}
              sent={false}
              text={<AnimatedText text={isThinkingText || ""} />}
              isAnswering={isAnswering}
              avatar="AI"
              timestamp=""
              isRobot={true}
              botName={themeConfig.chatbotName}
              onLike={() => {}}
              onDislike={() => {}}
            />
          </div>
        )}

        <div
          ref={messagesEndRef}
          className="mt-5 h-5"
          style={{ scrollMarginBottom: "100px" }}
        />
      </div>
    );
  }
);

MessageContainer.displayName = "MessageContainer";

export default MessageContainer;
